/**
 * Typed parser for the server-owned invoice merge plan
 * (SPEC-invoice-merge-history-2026-10-02 R1).
 *
 * The BACKEND decides eligibility, survivor, money, warnings and the digest
 * (backend/services/invoice_merge_plan.py). The client only parses and displays
 * the plan and echoes back what the reviewer chose. Every object is .strict():
 * a key the server adds that this parser does not know fails the contract test
 * (src/lib/test-fixtures/invoice-merge-plan.json is a REAL plan produced by the
 * real API, and the backend hermetic test pins its shape), so a new field can
 * never be silently stripped the way Zod strips unknown keys elsewhere in this
 * app.
 *
 * Money is always a canonical two-decimal STRING ("7875.00"), never a float.
 * No eligibility, survivor or balance arithmetic happens here, and none happens anywhere in the
 * reviewed path: R4's review panel renders the plan the server returned (see
 * components/invoices/InvoiceMergeReview.tsx).
 *
 * Free of react-query and api imports (only zod) so it is unit-testable alone.
 */
import { z } from 'zod';

export const MERGE_PLAN_VERSION = 1;

/** The six production lifecycle wire values (backend schemas.invoices.InvoiceStatus). */
export const MERGE_LIFECYCLE_STATES = [
  'draft',
  'ready',
  'sent',
  'partially_paid',
  'paid',
  'cancelled',
] as const;

const money = z.string().regex(/^\d+\.\d{2}$/, 'money must be a two-decimal string');
const nullableString = z.string().nullable();

const mergeSourceSchema = z
  .object({
    role: z.enum(['survivor', 'absorbed']),
    invoice_id: z.string(),
    invoice_number: z.string(),
    // A string, not the six-value enum: an unrecognised status arrives with a blocking entry.
    status: z.string(),
    version: z.string(),
    invoice_type: z.string(),
    organization_id: z.string(),
    organization_name: nullableString,
    line_count: z.number().int(),
    payment_count: z.number().int(),
    lines_total: money,
  })
  .strict();

const mergeCapConflictSchema = z
  .object({
    line_id: z.string(),
    billable_item_id: z.string(),
    reason: z.string().optional(),
    other_invoice_id: nullableString.optional(),
    other_invoice_number: nullableString.optional(),
    message: z.string(),
  })
  .passthrough();

const mergeBlockingSchema = z
  .object({
    code: z.string(),
    message: z.string(),
    /** False for D1 (draw) refusals and every other hard conflict. */
    overridable: z.boolean(),
    invoice_id: z.string().optional(),
    current_invoice_id: z.string().optional(),
    retryable: z.boolean().optional(),
    draw_summary: z
      .object({ gross: money, holdback: money, net: money })
      .strict()
      .nullable()
      .optional(),
    conflicts: z.array(mergeCapConflictSchema).optional(),
    cached: money.optional(),
    ledger: money.optional(),
  })
  .strict();

const mergeFieldOptionSchema = z
  .object({
    source: z.enum(['survivor', 'absorbed', 'unset', 'combined', 'edited']),
    value: z.unknown(),
    display: z.string(),
    requires_value: z.boolean().optional(),
  })
  .strict();

const mergeChoiceSchema = z.object({ source: z.string(), value: z.unknown().optional() }).strict();

const mergeFieldSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    kind: z.enum(['text', 'bool', 'date', 'id', 'recipients']),
    consumer: z.string(),
    survivor_value: z.unknown(),
    absorbed_value: z.unknown(),
    survivor_display: z.string(),
    absorbed_display: z.string(),
    equal: z.boolean(),
    resolution: z.enum(['same', 'chosen', 'choice_required']),
    options: z.array(mergeFieldOptionSchema),
    suggested_source: nullableString,
    selected: mergeChoiceSchema.nullable(),
    resolved_value: z.unknown(),
  })
  .strict();

const mergeLineSchema = z
  .object({
    id: z.string(),
    description: z.string(),
    amount: money,
    tax_amount: money,
    tax_rate: nullableString,
    quantity: nullableString,
    unit_price: nullableString,
    unit_type: nullableString,
    category: nullableString,
    billable_item_id: nullableString,
    original_invoice_id: nullableString,
    original_invoice_number: nullableString,
    original_provenance: z.string(),
    from: z.enum(['survivor', 'absorbed']),
  })
  .strict();

const mergePaymentSchema = z
  .object({
    id: z.string(),
    amount: money,
    status: z.enum(['active', 'voided']),
    paid_at: nullableString,
    method: nullableString,
    reference: nullableString,
    recorded_by: nullableString,
    voided_at: nullableString,
    voided_by: nullableString,
    void_reason: nullableString,
    original_invoice_id: nullableString,
    original_invoice_number: nullableString,
    original_provenance: z.string(),
    provider: nullableString,
    provider_payment_intent_id: nullableString,
    current_owner_before: z.string(),
    from: z.enum(['survivor', 'absorbed']),
    moves_to_combined_invoice: z.boolean(),
  })
  .strict();

const mergeMoneySchema = z
  .object({
    fee: money,
    tax: money,
    total: money,
    received: money,
    applied: money,
    outstanding: money,
    excess: money,
    per_source: z.array(
      z
        .object({
          invoice_id: z.string(),
          invoice_number: z.string(),
          status: z.string(),
          cached_total: money,
          lines_total: money,
          cached_amount_paid: money,
          ledger_received: money,
        })
        .strict(),
    ),
  })
  .strict();

const mergeResultSchema = z
  .object({
    invoice_id: z.string(),
    invoice_number: z.string(),
    status: z.string(),
    previous_statuses: z.record(z.string(), z.string()),
    needs_review_send: z.boolean(),
    current_version_sent: z.boolean(),
  })
  .strict();

const mergeIssuanceSchema = z
  .object({
    sources: z.array(
      z
        .object({
          invoice_id: z.string(),
          invoice_number: z.string(),
          historical_send_count: z.number().int(),
          last_sent_at: nullableString,
          email_sent_at: nullableString,
          ever_sent: z.boolean(),
          document_revision_count: z.number().int(),
          /** Sends Keystone itself recorded; zero with `ever_sent` means a person marked the invoice Sent. */
          keystone_send_count: z.number().int().default(0),
          payment_status: z.string(),
        })
        .strict(),
    ),
    result: z.object({ state: z.string(), historical_sends_retained: z.number().int() }).strict(),
  })
  .strict();

const mergeWarningSchema = z
  .object({
    code: z.string(),
    severity: z.enum(['info', 'notice', 'warning']),
    message: z.string(),
    invoice_id: z.string().optional(),
  })
  .strict();

const mergeAcknowledgementSchema = z
  .object({
    code: z.string(),
    message: z.string(),
    required: z.boolean(),
    acknowledged: z.boolean(),
    invoice_id: z.string().optional(),
    amount: money.optional(),
    fields: z.array(z.string()).optional(),
  })
  .strict();

/**
 * The project-scope defaults the existing selective-change machinery writes and invoice creation reads
 * (R2; R5 iteration 2 added the address, the DBA on its own and hide-DBA): the billed client with its
 * DBA, the recipient set, the custom billing address, the DBA and hide-DBA.
 */
export const FUTURE_DEFAULT_FIELDS = ['payer', 'recipients', 'billing_address', 'establishment', 'hide_dba'] as const;
export type FutureDefaultField = (typeof FUTURE_DEFAULT_FIELDS)[number];

const mergeFutureDispositionSchema = z
  .object({
    field: z.string(),
    label: z.string(),
    disposition: z.enum(['future_project_default', 'this_pair_only']),
    resolved_display: z.string(),
  })
  .strict();

const mergeFutureDefaultsPlanSchema = z
  .object({
    project_id: z.string(),
    fields: z.array(z.enum(FUTURE_DEFAULT_FIELDS)),
    dispositions: z.array(mergeFutureDispositionSchema),
    this_pair_only: z.array(z.string()),
  })
  .strict();

/**
 * The named project's CURRENT future-invoice defaults and the existing invoices a future default
 * leaves untouched (preview envelope, outside the digest). Informational: it lets the review say
 * what a future default would replace, including an explicit clear.
 */
export const mergeProjectDefaultsSchema = z
  .object({
    project_id: z.string(),
    project_name: nullableString,
    scopes: z.array(
      z
        .object({
          client_scope_id: z.string(),
          organization_id: z.string(),
          organization_name: nullableString,
          is_primary: z.boolean(),
          // The billed client a NEW invoice starts from (primary, else earliest-created): CHR-R5-13.
          is_new_invoice_default: z.boolean().optional(),
          establishment_id: z.number().int().nullable(),
          establishment_name: nullableString,
          establishment_cleared: z.boolean(),
          recipients: z.array(
            z
              .object({
                contact_id: nullableString,
                role: z.string(),
                name: z.string(),
                email: z.string(),
              })
              .strict(),
          ),
          recipients_cleared: z.boolean(),
          billing_address_default_source: z.string().nullable().optional(),
          billing_address_default: z.string().nullable().optional(),
          hide_dba_default: z.boolean().nullable().optional(),
        })
        .strict(),
    ),
    excluded_invoice_count: z.number().int(),
    excluded_invoices: z.array(
      z.object({ invoice_id: z.string(), invoice_number: z.string(), status: z.string() }).strict(),
    ),
  })
  .strict();

export const mergePlanSchema = z
  .object({
    plan_version: z.number().int(),
    survivor: z.object({ invoice_id: z.string(), invoice_number: z.string() }).strict(),
    absorbed: z.object({ invoice_id: z.string(), invoice_number: z.string() }).strict(),
    survivor_rule: z.string(),
    sources: z.array(mergeSourceSchema).length(2),
    blocking: z.array(mergeBlockingSchema),
    can_apply: z.boolean(),
    fields: z.array(mergeFieldSchema),
    lines: z.array(mergeLineSchema),
    payments: z.array(mergePaymentSchema),
    money: mergeMoneySchema,
    result: mergeResultSchema,
    issuance: mergeIssuanceSchema,
    warnings: z.array(mergeWarningSchema),
    required_acknowledgement_codes: z.array(z.string()),
    choices: z.record(z.string(), mergeChoiceSchema),
    digest: z.string().regex(/^[0-9a-f]{64}$/, 'digest must be 64 lowercase hex characters'),
    acknowledgements: z.array(mergeAcknowledgementSchema),
    missing_choices: z.array(z.string()),
    missing_acknowledgements: z.array(z.string()),
    ready_to_apply: z.boolean(),
    /** Present only when the reviewer opted into a future default for the named project. */
    future_defaults: mergeFutureDefaultsPlanSchema.optional(),
  })
  .strict();

export type MergePlan = z.infer<typeof mergePlanSchema>;
export type MergePlanField = z.infer<typeof mergeFieldSchema>;
export type MergePlanBlocking = z.infer<typeof mergeBlockingSchema>;
export type MergePlanPayment = z.infer<typeof mergePaymentSchema>;
export type MergePlanSource = z.infer<typeof mergeSourceSchema>;
export type MergeProjectDefaults = z.infer<typeof mergeProjectDefaultsSchema>;
export type MergeFutureDefaultsPlan = z.infer<typeof mergeFutureDefaultsPlanSchema>;

/** POST /api/invoices/merge-preview envelope (the wrapper is lenient; the plan is strict). */
export const mergePreviewResponseSchema = z.object({
  success: z.boolean(),
  plan: mergePlanSchema,
  project_defaults: mergeProjectDefaultsSchema.nullable().optional(),
});

/** The committed apply result (backend services/invoice_merge_execution.py `_write_merge`). */
export const mergeApplyResultSchema = z
  .object({
    operation_key: z.string(),
    operation_id: z.string(),
    plan_digest: z.string(),
    plan_version: z.number().int(),
    edge_id: z.string(),
    survivor_invoice_id: z.string(),
    survivor_invoice_number: z.string(),
    absorbed_invoice_id: z.string(),
    absorbed_invoice_number: z.string(),
    result_status: z.string(),
    money: z
      .object({ total: money, received: money, applied: money, outstanding: money, excess: money })
      .strict(),
    moved_line_ids: z.array(z.string()),
    moved_payment_ids: z.array(z.string()),
    line_count: z.number().int(),
    payment_count: z.number().int(),
    dismissed_followup_ids: z.array(z.string()),
    fields_written: z.array(z.string()),
    future_defaults_applied: z.array(z.string()),
    current_version: z.string(),
    effects: z
      .object({
        email_sent: z.boolean(),
        refund_issued: z.boolean(),
        reminder_created: z.boolean(),
        dropbox_deleted: z.boolean(),
        provider_called: z.boolean(),
        bank_funds_moved: z.boolean(),
      })
      .strict(),
    replayed: z.boolean(),
  })
  .strict();

export const mergeApplyResponseSchema = z.object({
  success: z.boolean(),
  result: mergeApplyResultSchema,
});

export type MergeApplyResult = z.infer<typeof mergeApplyResultSchema>;

/** Parse the plan or throw a ZodError naming the offending path. */
export function parseMergePlan(data: unknown): MergePlan {
  return mergePlanSchema.parse(data);
}

export function safeParseMergePlan(data: unknown) {
  return mergePlanSchema.safeParse(data);
}

export interface MergeChoiceInput {
  source: string;
  value?: unknown;
}

export interface MergeFutureDefaultsRequest {
  project_id: string;
  fields: FutureDefaultField[];
}

export interface MergePreviewRequest {
  invoice_id_a: string;
  invoice_id_b: string;
  choices?: Record<string, MergeChoiceInput>;
  acknowledged?: string[];
  /** Opt-in only: omitted means this pair only (the default). */
  future_defaults?: MergeFutureDefaultsRequest;
}

/** The PDF preview is bound to the reviewed plan (digest + versions) and has no operation key. */
export interface MergePreviewPdfRequest extends MergePreviewRequest {
  plan_version: number;
  plan_digest: string;
  source_versions: Record<string, string>;
}

export interface MergeApplyRequest extends MergePreviewPdfRequest {
  operation_key: string;
}

/**
 * Build the reviewed-apply body from the plan the reviewer actually saw. The apply is
 * bound to that plan's digest and to each source invoice's version; the server rebuilds
 * the plan and refuses, with zero effects, if anything differs. The tenant is never sent.
 */
export function buildMergePdfPreviewRequest(
  plan: MergePlan,
  options: { futureDefaults?: MergeFutureDefaultsRequest | null } = {},
): MergePreviewPdfRequest {
  return {
    invoice_id_a: plan.survivor.invoice_id,
    invoice_id_b: plan.absorbed.invoice_id,
    choices: plan.choices,
    acknowledged: [],
    ...(options.futureDefaults ? { future_defaults: options.futureDefaults } : {}),
    plan_version: plan.plan_version,
    plan_digest: plan.digest,
    source_versions: Object.fromEntries(plan.sources.map((s) => [s.invoice_id, s.version])),
  };
}

export function buildMergeApplyRequest(
  plan: MergePlan,
  options: {
    operationKey: string;
    acknowledged?: string[];
    futureDefaults?: MergeFutureDefaultsRequest | null;
  },
): MergeApplyRequest {
  return {
    ...buildMergePdfPreviewRequest(plan, { futureDefaults: options.futureDefaults }),
    acknowledged: options.acknowledged ?? [],
    operation_key: options.operationKey,
  };
}

/** Blocking conflicts the reviewer cannot override (every one today, D1 included). */
export function hardBlockers(plan: MergePlan): MergePlanBlocking[] {
  return plan.blocking.filter((b) => !b.overridable);
}

/** Paid money held above the combined total, as a canonical string ("0.00" when none). */
export function plannedExcess(plan: MergePlan): string {
  return plan.money.excess;
}

// Reads the server's cached paid figure only to show a reconciliation line; the ledger
// (money.received) is what the plan uses, and no ratio or balance is computed here.
export function cachedAmountPaidBySource(plan: MergePlan): Record<string, string> {
  return Object.fromEntries(plan.money.per_source.map((s) => [s.invoice_id, s.cached_amount_paid]));
}
